import { getApiClient } from '../axios-config';
import Logger from '../../../utils/logger';

/**
 * Bids API Service (ticket #295)
 * Handles all API calls for the Bids workspace. Super-admins only; the server
 * returns 404 when BIDS_ENABLED is off.
 *
 * Errors are not caught here: callers read error.response.status and
 * error.response.data.field (400 field errors, 409 duplicate bid number).
 */
class BidsAPI {
  /**
   * List bids
   * @param {string} token - JWT authentication token
   * @param {Object} [options]
   * @param {string} [options.group='all'] - open | won | closed | all
   * @param {number} [options.divisionId] - only this division
   * @param {number} [options.limit] - max rows (1-50); with won / closed the
   *   newest updates come first
   * @returns {Promise<Array>} Array of bid objects
   */
  static async list(token, { group = 'all', divisionId, limit } = {}) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/bids', {
      params: { group, divisionId, limit },
    });
    Logger.debug('BidsAPI.list - Response structure', {
      hasData: !!response.data.data,
      bidsCount: response.data.data?.bids?.length,
    });
    return response.data.data.bids || [];
  }

  /**
   * Get one bid
   * @param {string} bidId - ID of the bid
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Bid object
   */
  static async get(bidId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/bids/${bidId}`);
    return response.data.data.bid;
  }

  /**
   * Create a bid (always starts as estimating)
   * @param {Object} bid - { divisionId, proposalNumber, name, bidDueAt, bidtracerId }
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created bid object
   */
  static async create(bid, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/bids', bid);
    return response.data.data.bid;
  }

  /**
   * Update a bid's editable fields
   * @param {string} bidId - ID of the bid
   * @param {Object} bid - { divisionId, proposalNumber, name, status, bidDueAt,
   *   projectId, bidtracerId }; projectId null clears the link
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated bid object
   */
  static async update(bidId, bid, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(`/api/bids/${bidId}`, bid);
    return response.data.data.bid;
  }

  /**
   * Get active divisions, in Estimate Log order
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of { id, code, name, jobNumber }
   */
  static async getDivisions(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/bids/divisions');
    return response.data.data.divisions || [];
  }
}

export default BidsAPI;
